import { isNull } from 'lodash/fp';
import React, { useCallback, useMemo } from 'react';
import { Options } from 'react-select';

import { useAppDispatch, useAppSelector } from '../../../../hooks/react-redux';
import { Dropdown, DropdownOption } from '../../../shared/dropdown/Dropdown';
import { Scrollable } from '../../../shared/scrollable/Scrollable';
import { Text } from '../../../shared/text/Text';
import { getAllDatasetDefinitions } from '../../dataset-builder/store';
import styles from '../DocumentName.module.scss';
import { SystemDocumentNameDB, getAllAgreementTypes, updateValue } from '../store';

const { red, primary } = styles;

interface GeneralProps {
    systemDocumentName: SystemDocumentNameDB;
    nonUniqueName: boolean;
}

export const General: React.FC<GeneralProps> = ({ systemDocumentName, nonUniqueName }) => {
    const { agreementType, agreementTypeId, documentName, datasetId, datasetTitle } = systemDocumentName;

    const dispatch = useAppDispatch();
    const datasetDefinitions = useAppSelector(getAllDatasetDefinitions);
    const agreementTypes = useAppSelector(getAllAgreementTypes);

    const updateDocumentNameValue = useCallback((key: keyof SystemDocumentNameDB, value: string | null | number | number[]) => dispatch(updateValue(key, value)), [dispatch]);

    const updateDataset = (option: DropdownOption | Options<DropdownOption> | null) => {
        let datasetId = null;
        let datasetTitle = '';
        if (!isNull(option)) {
            datasetId = parseInt((option as DropdownOption).value);
            datasetTitle = (option as DropdownOption).label;
        }
        updateDocumentNameValue('datasetId', datasetId);
        updateDocumentNameValue('datasetTitle', datasetTitle);
    };

    const updateAgreementType = (option: DropdownOption | Options<DropdownOption> | null) => {
        let agreementTypeId = null;
        let agreementType = null;
        if (!isNull(option)) {
            agreementTypeId = parseInt((option as DropdownOption).value);
            agreementType = (option as DropdownOption).label;
        }
        updateDocumentNameValue('agreementTypeId', agreementTypeId);
        updateDocumentNameValue('agreementType', agreementType);
    };

    const datasetLinked = datasetId ? { value: datasetId.toString(), label: datasetTitle! } : null;
    const agreementTypeLinked = agreementType && agreementTypeId ? { value: agreementTypeId.toString(), label: agreementType } : null;

    const datasetOptions = useMemo(() => datasetDefinitions
        .map(({ datasetId, datasetTitle }) => ({ value: datasetId.toString(), label: datasetTitle }))
        .sort((a, b) => a.label.localeCompare(b.label)), [datasetDefinitions]);

    const agreementTypeOptions = agreementTypes
        .map(({ agreementTypeId, name }) => ({ value: agreementTypeId.toString(), label: name }))
        .sort((a, b) => a.label.localeCompare(b.label));

    return (
        <div className={styles.inputsWrapper}>
            <Scrollable>
                <Text
                    label='Document Name'
                    testId='document-name'
                    placeholder='Document Name...'
                    value={documentName}
                    onChange={e => updateDocumentNameValue('documentName', e.target.value)}
                    borderColour={nonUniqueName ? red : primary}
                />
                <Dropdown
                    disabled={false}
                    value={datasetLinked}
                    options={datasetOptions}
                    testId='document-name-dataset'
                    onChange={updateDataset}
                    label='Dataset'
                    menuPortalTarget={document.body}
                />
                <div className={styles.modalInputSpacer} />
                <Dropdown
                    disabled={false}
                    value={agreementTypeLinked}
                    options={agreementTypeOptions}
                    testId='document-name-agreement-type'
                    onChange={updateAgreementType}
                    label='Agreement Type'
                    menuPortalTarget={document.body}
                />
            </Scrollable>
        </div>
    );
};
